// assets/fbt.js - Full merged script for Frequently Bought Together
// - Prefers section-level product picks (blocks), then product metafield picks,
//   then Shopify recommendations, then collection fallback.
// - Robust price normalization, dynamic CTA text, select-all, mobile behavior.
// - Adds selected products sequentially to cart and dispatches 'fbt:added' event.

document.addEventListener("DOMContentLoaded", () => {
  const roots = document.querySelectorAll(".fbt-section");
  roots.forEach(initFBT);
});

function initFBT(root) {
  if (!root) return;

  // Data attributes
  const productId = root.dataset.productId;
  const productHandle = root.dataset.productHandle;
  const collectionHandle = root.dataset.collectionHandle;
  const chosenHandlesJson = root.dataset.chosenHandles || "[]"; // product metafield picks
  const selectedHandlesJson = root.dataset.selectedHandles || "[]"; // section block picks

  // parse lists (safe)
  let chosenHandles = [];
  let sectionSelected = [];
  try { chosenHandles = JSON.parse(chosenHandlesJson || "[]"); } catch (e) { chosenHandles = []; }
  try { sectionSelected = JSON.parse(selectedHandlesJson || "[]"); } catch (e) { sectionSelected = []; }

  // max items (clamped)
  let parsed = parseInt(root.dataset.maxItems, 10);
  if (isNaN(parsed)) parsed = 3;
  const maxItems = Math.min(Math.max(parsed, 1), 6);

  // DOM references expected in section file
  const list = root.querySelector(".fbt-list");
  const loader = root.querySelector(".fbt-loader");
  const actions = root.querySelector(".fbt-actions");
  const addBtn = root.querySelector(".fbt-add-selected");
  const msg = root.querySelector(".fbt-message");
  const totalBox = root.querySelector(".fbt-total");
  const totalAmount = root.querySelector(".fbt-total-amount");

  // Ensure layout wrapper exists (desktop: list left, side right)
  ensureLayout(root, list, totalBox, actions);

  // Helpers --------------------------------------------------------

  // Normalize a price-like value to integer cents.
  function normalizePriceToCents(value) {
    if (value === undefined || value === null) return 0;

    if (typeof value === "number") {
      if (value > 1000) return Math.round(value);
      return Math.round(value * 100);
    }

    const s = String(value).trim();
    if (!s.length) return 0;
    const cleaned = s.replace(/[^\d.-]/g, "");
    if (!cleaned.length) return 0;
    if (cleaned.indexOf(".") !== -1) {
      const n = Number(cleaned);
      if (isNaN(n)) return 0;
      return Math.round(n * 100);
    }
    const n = Number(cleaned);
    if (isNaN(n)) return 0;
    if (n > 1000) return Math.round(n);
    return Math.round(n * 100);
  }

  // Format cents into currency string. Prefer Shopify.formatMoney if available.
  function formatMoneyFromCents(cents) {
    if (typeof cents !== "number") cents = Number(cents) || 0;
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function" && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
    } catch (e) { /* ignore */ }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "INR";
    try {
      return (cents / 100).toLocaleString(undefined, { style: "currency", currency });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(str) {
    if (str === undefined || str === null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // UI helpers -----------------------------------------------------

  function showError(message, details) {
    if (loader && loader.parentNode) loader.remove();
    if (list) list.innerHTML = `<div class="fbt-empty">${escapeHtml(message)}</div>`;
    if (details) console.warn("FBT: " + message, details);
    else console.warn("FBT: " + message);
  }

  function updateCTA(count, sumCents) {
    if (!addBtn) return;
    if (count && count > 0) {
      const formatted = formatMoneyFromCents(sumCents || 0);
      addBtn.textContent = `Buy all ${count}: ${formatted}`;
      addBtn.disabled = false;
    } else {
      addBtn.textContent = "Add selected to cart";
      addBtn.disabled = true;
    }
  }

  function recomputeTotal() {
    if (!list) return;
    const boxes = list.querySelectorAll(".fbt-checkbox");
    let sum = 0;
    let count = 0;
    boxes.forEach(b => {
      if (!b.checked) return;
      count++;
      const cents = normalizePriceToCents(b.dataset.priceCents || 0);
      sum += cents;
    });

    if (totalAmount) {
      if (sum > 0) {
        totalAmount.textContent = formatMoneyFromCents(sum);
        if (totalBox) totalBox.hidden = false;
      } else {
        if (totalBox) totalBox.hidden = true;
      }
    }

    updateCTA(count, sum);
  }

  // NEW: improved wireAfterRender (select-all indeterminate + checkbox wiring)
  function wireAfterRender() {
    if (actions) actions.hidden = false;

    // Select-all: find the checkbox inside actions
    const internalCheckAll = root.querySelector(".fbt-check-all");

    // Replace with cloned node to remove previous listeners
    if (internalCheckAll && internalCheckAll.parentNode) {
      const cloned = internalCheckAll.cloneNode(true);
      internalCheckAll.parentNode.replaceChild(cloned, internalCheckAll);
    }
    const freshCheckAll = root.querySelector(".fbt-check-all");

    // Helper to refresh select-all state
    function refreshSelectAllState() {
      const boxes = Array.from(list.querySelectorAll(".fbt-checkbox"));
      if (!boxes.length) {
        if (freshCheckAll) { freshCheckAll.checked = false; freshCheckAll.indeterminate = false; }
        return;
      }
      const checkedCount = boxes.filter(b => b.checked).length;
      if (!freshCheckAll) return;
      if (checkedCount === 0) {
        freshCheckAll.checked = false;
        freshCheckAll.indeterminate = false;
      } else if (checkedCount === boxes.length) {
        freshCheckAll.checked = true;
        freshCheckAll.indeterminate = false;
      } else {
        freshCheckAll.checked = false;
        freshCheckAll.indeterminate = true;
      }
    }

    // Wire freshCheckAll change -> toggle every item and recompute
    if (freshCheckAll) {
      freshCheckAll.addEventListener("change", (e) => {
        const boxes = list.querySelectorAll(".fbt-checkbox");
        boxes.forEach(b => b.checked = e.target.checked);
        recomputeTotal();
      });
    }

    // Rewire item checkboxes: clone to remove old listeners then add new ones
    const boxes = Array.from(list.querySelectorAll(".fbt-checkbox"));
    boxes.forEach(oldBox => {
      const newBox = oldBox.cloneNode(true);
      oldBox.parentNode.replaceChild(newBox, oldBox);
      newBox.addEventListener("change", () => {
        recomputeTotal();
        refreshSelectAllState();
      });
    });

    // Initial state: refresh select-all and compute totals
    refreshSelectAllState();
    recomputeTotal();
  }

  // Add-to-cart: sequentially add variant ids for selected boxes
  async function addSelectedToCart() {
    if (!list || !addBtn || !msg) return;
    msg.textContent = "";
    const boxes = Array.from(list.querySelectorAll(".fbt-checkbox:checked"));
    if (!boxes.length) {
      msg.style.color = "#b00";
      msg.textContent = "Select at least one item.";
      return;
    }

    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = "Adding…";

    try {
      for (const b of boxes) {
        const vid = Number(b.dataset.vid);
        if (!vid) {
          console.warn("FBT: skipping item with no variant id", b);
          continue;
        }
        const resp = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: vid, quantity: 1 })
        });
        if (!resp.ok) {
          const j = await resp.json().catch(()=>({}));
          throw new Error(j.description || "Add to cart failed");
        }
      }

      msg.style.color = "#0a0";
      msg.textContent = "Added to cart.";
      document.dispatchEvent(new CustomEvent("fbt:added", { detail: { count: boxes.length } }));
    } catch (err) {
      console.error("FBT add error:", err);
      msg.style.color = "#b00";
      msg.textContent = "Could not add items.";
    } finally {
      addBtn.disabled = false;
      addBtn.textContent = originalText || "Add selected to cart";
    }
  }

  // Attach addBtn handler (clone to remove prior listeners)
  if (addBtn && addBtn.parentNode) {
    const clonedBtn = addBtn.cloneNode(true);
    addBtn.parentNode.replaceChild(clonedBtn, addBtn);
    const newAdd = root.querySelector(".fbt-add-selected");
    if (newAdd) newAdd.addEventListener("click", addSelectedToCart);
  }

  // Data fetching & rendering ------------------------------------

  async function fetchProductsByHandles(handles) {
    const products = [];
    for (const h of handles.slice(0, maxItems)) {
      if (!h) continue;
      try {
        const resp = await fetch(`/products/${encodeURIComponent(h)}.js`);
        if (!resp.ok) {
          console.warn("FBT: /products/{handle}.js fetch failed for", h, resp.status);
          continue;
        }
        const prod = await resp.json().catch(()=>null);
        if (prod) products.push(prod);
      } catch (err) {
        console.warn("FBT: handle fetch error for", h, err);
      }
    }
    return products;
  }

  function renderProductsFromCollectionLike(products) {
    if (loader && loader.parentNode) loader.remove();
    if (!list) return;
    list.innerHTML = "";

    products.forEach((p, idx) => {
      const variant = (p.variants && p.variants[0]) ? p.variants[0] : null;
      const priceCents = variant ? normalizePriceToCents(variant.price || variant.compare_at_price || 0) : 0;
      const img = (p.image && p.image.src) || (p.images && (typeof p.images[0] === "string" ? p.images[0] : (p.images[0] && p.images[0].src))) || '/assets/no-image.svg';
      const title = p.title || p.handle || "Product";
      const vid = variant ? variant.id : "";

      const item = document.createElement("div");
      item.className = "fbt-item";
      item.innerHTML = `
        <input type="checkbox" class="fbt-checkbox" data-vid="${vid}" data-price-cents="${priceCents}" checked>
        <img src="${escapeAttr(img)}" alt="${escapeAttr(title)}">
        <div class="meta">
          <div class="fbt-title">${escapeHtml(title)}</div>
          <div class="price">${formatMoneyFromCents(priceCents)}</div>
        </div>
      `;
      list.appendChild(item);

      if (idx < products.length - 1) {
        const plus = document.createElement("div");
        plus.className = "fbt-plus";
        plus.textContent = "+";
        list.appendChild(plus);
      }
    });

    wireAfterRender();
  }

  function renderProductsFromRecommendations(recs) {
    if (loader && loader.parentNode) loader.remove();
    if (!list) return;
    list.innerHTML = "";
    recs.forEach((p, idx) => {
      const variant = p.variants?.[0] || null;
      const priceCents = variant ? normalizePriceToCents(variant.price || variant.compare_at_price || 0) : 0;
      const img = p.image?.src || '/assets/no-image.svg';
      const title = p.title || p.handle || "Product";
      const vid = variant ? variant.id : "";

      const item = document.createElement("div");
      item.className = "fbt-item";
      item.innerHTML = `
        <input type="checkbox" class="fbt-checkbox" data-vid="${vid}" data-price-cents="${priceCents}" checked>
        <img src="${escapeAttr(img)}" alt="${escapeAttr(title)}">
        <div class="meta">
          <div class="fbt-title">${escapeHtml(title)}</div>
          <div class="price">${formatMoneyFromCents(priceCents)}</div>
        </div>
      `;
      list.appendChild(item);

      if (idx < recs.length - 1) {
        const plus = document.createElement("div");
        plus.className = "fbt-plus";
        plus.textContent = "+";
        list.appendChild(plus);
      }
    });

    wireAfterRender();
  }

  (async function loadSourceAndRender() {
    if (!productId) {
      showError("Recommendations could not be loaded: product ID missing.");
      return;
    }

    const blockHandles = Array.isArray(sectionSelected) ? sectionSelected.filter(Boolean).slice(0, maxItems) : [];
    if (blockHandles.length) {
      console.info("FBT: using section block-selected handles:", blockHandles);
      const products = await fetchProductsByHandles(blockHandles);
      if (products.length) {
        renderProductsFromCollectionLike(products);
        return;
      } else {
        console.info("FBT: section block picks returned no products (check handles).");
      }
    }

    const chosen = Array.isArray(chosenHandles) ? chosenHandles.filter(Boolean).slice(0, maxItems) : [];
    if (chosen.length) {
      console.info("FBT: using product metafield chosen handles:", chosen);
      const products = await fetchProductsByHandles(chosen);
      if (products.length) {
        renderProductsFromCollectionLike(products);
        return;
      } else {
        console.info("FBT: product metafield picks returned no products.");
      }
    }

    try {
      const recUrl = `/recommendations/products?product_id=${encodeURIComponent(productId)}`;
      console.info("FBT fetching recommendations:", recUrl);
      const r = await fetch(recUrl);
      if (!r.ok) throw new Error(`Recommendations endpoint returned ${r.status}`);
      const data = await r.json().catch(()=>null);
      const recs = (data && data.products) ? data.products.slice(0, maxItems) : [];
      if (recs.length) {
        renderProductsFromRecommendations(recs);
        return;
      } else {
        console.info("FBT: recommendations returned 0 products.");
      }
    } catch (err) {
      console.info("FBT recommendations failed:", err);
    }

    if (collectionHandle) {
      try {
        console.info("FBT: trying collection fallback:", collectionHandle);
        const colUrl = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${maxItems}`;
        const rc = await fetch(colUrl);
        if (rc.ok) {
          const colData = await rc.json().catch(()=>null);
          const products = (colData && colData.products) ? colData.products.filter(p => p.handle !== productHandle).slice(0, maxItems) : [];
          if (products.length) {
            renderProductsFromCollectionLike(products);
            return;
          }
        } else {
          console.info("FBT collection fetch returned", rc.status);
        }
      } catch (err) {
        console.info("FBT collection fetch error:", err);
      }
    }

    if (loader && loader.parentNode) loader.remove();
    if (list) list.innerHTML = '<div class="fbt-empty">Recommendations could not be loaded.</div>';
  })();

  // done init
}

// ----------------- Utility: ensure layout container exists --------------
function ensureLayout(root, listEl, totalBoxEl, actionsEl) {
  if (!root) return;
  if (root.querySelector(".fbt-inner-wrap")) return;

  const inner = document.createElement("div");
  inner.className = "fbt-inner-wrap";

  const side = document.createElement("div");
  side.className = "fbt-side";

  if (listEl && listEl.parentNode) {
    listEl.parentNode.insertBefore(inner, listEl);
    inner.appendChild(listEl);
  } else {
    root.appendChild(inner);
  }

  if (totalBoxEl && totalBoxEl.parentNode) {
    side.appendChild(totalBoxEl);
  }
  if (actionsEl && actionsEl.parentNode) {
    side.appendChild(actionsEl);
  }

  inner.appendChild(side);
}
